<template>
  <div class="tw:rounded-lg tw:border custom-shadow tw:p-5">
    <p class="tw:font-bold">
      {{ $t(header) }}
    </p>
    <p class="tw:text-xs">{{ $t(subtextLine) }}</p>
    <div class="tw:flex tw:my-4 tw:pb-4 tw:border-b tw:border-gray-300 tw:flex tw:items-center">
      <div v-if="!defaultPaymentMethod" class="tw:animate-pulse tw:rounded tw:bg-gray-300 tw:h-[35px] tw:w-full" />
      <div v-if="defaultPaymentMethod?.type === 'WIRE_TRANSFER'" class="tw:flex tw:items-center tw:gap-x-2">
        <BankTransferIcon class="tw:w-5 tw:h-[14px]" />
        <span class="tw:grow">{{ $t('rc_fop_bank_transfer') }}</span>
      </div>
      <div v-if="defaultPaymentMethod?.type === 'SEPA'">
        <span class="tw:grow">{{
          `SEPA - ${defaultPaymentMethod.accountNumber} - ${defaultPaymentMethod.accountHolderInfo?.accountHolderName}`
        }}</span>
      </div>
      <div v-else-if="defaultPaymentMethod?.type === 'CREDIT_CARD'" class="tw:flex tw:gap-x-2 tw:flex-wrap">
        <div>
          <img
            :src="getCreditCardLogoUrl(defaultPaymentMethod.creditCardType)"
            alt="Credit card logo"
            height="24"
            width="38"
            class="shadow-[0_0_1px_0_rgba(41,52,58,0.75)] tw:mr-2 tw:inline"
          />
          <span>{{ defaultPaymentMethod.cardNumber }}</span>
        </div>
      </div>
    </div>
    <div
      class="tw:flex tw:text-xs tw:items-baseline tw:mt-6"
      v-if="!defaultPaymentMethod?.expired && !cardExpiresSoon && defaultPaymentMethod?.type !== 'WIRE_TRANSFER'"
    >
      <i18n-t keypath="payment_method_tip" tag="p" class="tw:text-xs">
        <template #ClickHere>
          <button class="redirect-btn tw:hover:cursor-pointer tw:hover:text-blue-800" @click="openBillingPage">
            {{ $t('click_here_billing') }}
          </button>
        </template>
      </i18n-t>
    </div>
    <div v-if="cardExpiresSoon && !defaultPaymentMethod?.expired">
      <SecondaryButton size="medium" class="tw:w-full" @click="openPaymentPageSoonExpired">{{
        `+ ${$t('payment_method_new_cta')}`
      }}</SecondaryButton>
    </div>
    <div v-else-if="defaultPaymentMethod?.expired || defaultPaymentMethod?.type === 'WIRE_TRANSFER'">
      <MainButton size="medium" class="tw:w-full" @click="openPaymentPageExpired">{{
        `+ ${$t('payment_method_new_cta')}`
      }}</MainButton>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import SecondaryButton from '@/components/base/buttons/SecondaryButton.vue'
import BankTransferIcon from '@/components/icons/BankTransferIcon.svg?component'

import appAnalytics from '@/utils/tracking'
import { selectedAccommodationId } from '@/layouts/queries'

const visaLogo = new URL('/src/assets/img/settings-payment/visa.png', import.meta.url)
const mastercardLogo = new URL('/src/assets/img/settings-payment/mastercard.png', import.meta.url)
const americanexpressLogo = new URL('/src/assets/img/settings-payment/americanexpress.png', import.meta.url)

const props = defineProps({
  defaultPaymentMethod: Object,
  cardExpiresSoon: Boolean,
  type: String,
})

const emit = defineEmits(['openPaymentPage'])
const openPaymentPage = () => emit('openPaymentPage')

const openPaymentPageExpired = () => {
  appAnalytics.track(appAnalytics.events.RC_RESUME_CAMPAIGN_ADD_NEW_PAYMENT_METHOD_EXPIRED_NOT_VALID, {
    accommodationId: selectedAccommodationId.value,
    paymentMethod: props.defaultPaymentMethod?.type,
    isExpired: props.defaultPaymentMethod?.expired,
    expirationDate: `${props.defaultPaymentMethod?.expirationMonth}/${props.defaultPaymentMethod?.expirationYear}`,
  })
  openPaymentPage()
}

const openPaymentPageSoonExpired = () => {
  appAnalytics.track(appAnalytics.events.RC_RESUME_CAMPAIGN_ADD_NEW_PAYMENT_METHOD_SOON_EXPIRED, {
    accommodationId: selectedAccommodationId.value,
    paymentMethod: props.defaultPaymentMethod?.type,
    isExpired: props.defaultPaymentMethod?.expired,
    expirationDate: `${props.defaultPaymentMethod?.expirationMonth}/${props.defaultPaymentMethod?.expirationYear}`,
  })
  openPaymentPage()
}

const openBillingPage = () => {
  appAnalytics.track(appAnalytics.events.RC_RESUME_CAMPAIGN_REDIRECT_TO_BILLING_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    paymentMethod: props.defaultPaymentMethod?.type,
    isExpired: props.defaultPaymentMethod?.expired,
    expirationDate: `${props.defaultPaymentMethod?.expirationMonth}/${props.defaultPaymentMethod?.expirationYear}`,
  })
  openPaymentPage()
}

const getCreditCardLogoUrl = type => {
  if (type === 'Visa') return visaLogo
  if (type === 'MasterCard') return mastercardLogo
  if (type === 'AmericanExpress') return americanexpressLogo
}

const header = computed(() => {
  if (props.defaultPaymentMethod?.expired) return 'payment_method_expired_header'
  else return 'payment_method_confirm_header'
})

const subtextLine = computed(() => {
  if (props.defaultPaymentMethod?.expired) return 'fop_expired_info'
  if (props.type === 'RATE_CONNECT' && props.defaultPaymentMethod?.type === 'WIRE_TRANSFER')
    return 'rc_fop_subtext_invalid'
  if (props.type === 'RATE_CONNECT' && !props.defaultPaymentMethod?.expired) return 'rc_fop_info'
  if (props.type === 'PLUS' && props.defaultPaymentMethod?.type === 'WIRE_TRANSFER') return 'plus_fop_subtext_invalid'
  if (props.type === 'PLUS' && !props.defaultPaymentMethod?.expired) return 'payment_method_confirm_subtext'
  else return ''
})
</script>
<style scoped>
.custom-shadow {
  box-shadow: 0px 1px 4px 0px #37454dbf;
}
.border {
  border: 8px solid var(--tw-color-blue-800);
}
p :deep(a),
span:deep(a),
.redirect-btn {
  text-decoration: underline;
}
</style>
